const ACCESS_TOKEN_NAME = "access_token";
const REFRESH_TOKEN_NAME = "refresh_token";
const MFA_EMAIL_NAME = "mfa_email";
const MFA_TOKEN_NAME = "mfa_token";
const LAST_OAUTH_PROVIDER_NAME = "last_oauth_provider";
const LAST_SIGN_IN_METHOD_KEY = "last_sign_in_method";

// Use sessionStorage instead of localStorage for better security
// sessionStorage is cleared when the tab is closed, reducing XSS attack window
export const getItem = (name: string) => {
	return sessionStorage.getItem(name);
};

export const setItem = (name: string, value: string) => {
	sessionStorage.setItem(name, value);
};

export const removeItem = (name: string) => {
	sessionStorage.removeItem(name);
};

export const getAccessToken = () => {
	return getItem(ACCESS_TOKEN_NAME);
};

export const getRefreshToken = () => {
	return getItem(REFRESH_TOKEN_NAME);
};

export const setAccessToken = (token: string) => {
	setItem(ACCESS_TOKEN_NAME, token);
};

export const setRefreshToken = (token: string) => {
	setItem(REFRESH_TOKEN_NAME, token);
};

export const deleteAccessToken = () => {
	removeItem(ACCESS_TOKEN_NAME);
};

export const deleteRefreshToken = () => {
	removeItem(REFRESH_TOKEN_NAME);
};

// MFA email storage (temporary, cleared after use)
export const getMfaEmail = () => {
	return getItem(MFA_EMAIL_NAME);
};

export const setMfaEmail = (email: string) => {
	setItem(MFA_EMAIL_NAME, email);
};

export const deleteMfaEmail = () => {
	removeItem(MFA_EMAIL_NAME);
};

// Pending MFA token issued by the OAuth callback (temporary, single-use)
export const getMfaToken = () => {
	return getItem(MFA_TOKEN_NAME);
};

export const setMfaToken = (token: string) => {
	setItem(MFA_TOKEN_NAME, token);
};

export const deleteMfaToken = () => {
	removeItem(MFA_TOKEN_NAME);
};

// Non-sensitive preferences can use localStorage so they persist across sessions.
export type OauthProvider = "google" | "github";

/** How the user last signed in on this browser (OAuth or email/password). */
export type LastSignInMethod = "email" | OauthProvider;

export const getLastSignInMethod = (): LastSignInMethod | null => {
	try {
		const value = localStorage.getItem(LAST_SIGN_IN_METHOD_KEY);
		if (value === "email" || value === "google" || value === "github") {
			return value;
		}
		const legacy = localStorage.getItem(LAST_OAUTH_PROVIDER_NAME);
		if (legacy === "google" || legacy === "github") return legacy;
		return null;
	} catch {
		return null;
	}
};

export const setLastSignInMethod = (method: LastSignInMethod) => {
	try {
		localStorage.setItem(LAST_SIGN_IN_METHOD_KEY, method);
		if (method === "google" || method === "github") {
			localStorage.setItem(LAST_OAUTH_PROVIDER_NAME, method);
		}
	} catch {
		// Ignore storage failures (private mode, disabled storage, etc.)
	}
};

export const getLastOauthProvider = (): OauthProvider | null => {
	const m = getLastSignInMethod();
	if (m === "google" || m === "github") return m;
	return null;
};

export const setLastOauthProvider = (provider: OauthProvider) => {
	setLastSignInMethod(provider);
};

export const deleteLastOauthProvider = () => {
	try {
		localStorage.removeItem(LAST_OAUTH_PROVIDER_NAME);
		localStorage.removeItem(LAST_SIGN_IN_METHOD_KEY);
	} catch {
		// Ignore
	}
};
